import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { lazy, Suspense, useState } from "react";
import { useBillingStatus, usePortal, useCheckout } from "@/features/billing/hooks/useBilling";
import { ApiErrorBanner } from "@/components/ui/ApiErrorBanner";
import { PageLoader } from "@/components/ui/PageLoader";
import { CurrentPlanCard } from "@/features/billing/components/CurrentPlanCard";
import { PastDueBanner } from "@/features/billing/components/PastDueBanner";
import { PricingSection } from "@/features/billing/components/PricingSection";
import { QuotasGrid } from "@/features/billing/components/QuotasGrid";
import { UsageCard, UsagePlaceholder } from "@/features/billing/components/UsageCard";

const MadHistoryChart = lazy(() =>
  import("@/features/billing/components/MadHistoryChart").then((mod) => ({
    default: mod.MadHistoryChart,
  })),
);

export const Route = createFileRoute("/_authenticated/billing")({
  component: BillingPage,
});

function BillingPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useBillingStatus();
  const portal = usePortal();
  const checkout = useCheckout();
  const [interval, setInterval] = useState<"monthly" | "annual">("monthly");

  const isPending = portal.isPending || checkout.isPending;

  const handleManageBilling = () => {
    portal.mutate(undefined, {
      onSuccess: (res) => {
        window.open(res.url, "_blank");
      },
    });
  };

  const handleCheckout = (plan: "pro" | "scale") => {
    checkout.mutate(
      { plan, interval },
      {
        onSuccess: (res) => {
          // Redirect in same window to Stripe Checkout
          window.location.href = res.url;
        },
      },
    );
  };

  return (
    <div className="mx-auto max-w-5xl relative">
      {/* Loading & Mutation Pending Overlay */}
      {isPending && (
        <div className="fixed inset-0 bg-void/60 backdrop-blur-sm z-50 flex flex-col items-center justify-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo border-t-transparent" />
          <p className="text-sm font-medium text-heading">
            {portal.isPending ? t("common.loading") : "Redirecting to checkout..."}
          </p>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-heading">{t("billing.title")}</h1>
        <p className="mt-1 text-sm text-muted">{t("billing.subtitle")}</p>
      </div>

      {/* Loading State */}
      {isLoading && <PageLoader />}

      {/* Error Banner */}
      <ApiErrorBanner error={error} className="mt-8" />

      {data && (
        <div className="mt-6 space-y-8">
          {/* Past due banner */}
          {data.plan.billing_status === "past_due" && <PastDueBanner />}

          {/* Current plan card */}
          <CurrentPlanCard
            data={data}
            onManageBilling={handleManageBilling}
            isPending={isPending}
          />

          {/* Conditional Layout: Free Tier vs Paid Tier */}
          {data.plan.tier === "free" ? (
            <>
              <PricingSection
                interval={interval}
                onIntervalChange={setInterval}
                onCheckout={handleCheckout}
                isPending={isPending}
              />
              <QuotasGrid quotas={data.quotas} title={`${t("billing.quotas")} (Free Tier)`} />
            </>
          ) : (
            <div className="space-y-6 animate-fadeIn">
              <QuotasGrid quotas={data.quotas} />
              {data.usage ? (
                <UsageCard usage={data.usage}>
                  <Suspense
                    fallback={<p className="mt-4 text-sm text-muted">{t("common.loading")}</p>}
                  >
                    <MadHistoryChart />
                  </Suspense>
                </UsageCard>
              ) : (
                <UsagePlaceholder />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
